// src/pages/StudentReport.jsx
import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-hot-toast";
import SummaryApi from "../common/index";
import { Eye, Download, X, FileDown, FileText, Printer, CheckCircle, XCircle, AlertCircle, FolderOpen } from "lucide-react";
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";
import jsPDF from "jspdf";
import html2canvas from "html2canvas";
import ghrLogo from "../assets/ghru-logo2.png";

const StudentReport = () => {
  const navigate = useNavigate();
  const [students, setStudents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedStudents, setSelectedStudents] = useState([]);
  const [selectAll, setSelectAll] = useState(false);
  const [enrollmentSearch, setEnrollmentSearch] = useState("");
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [selectedCourse, setSelectedCourse] = useState("All");
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 25;

  const [selectedStudent, setSelectedStudent] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [receiptImageUrl, setReceiptImageUrl] = useState(null);
  const [loadingReceipt, setLoadingReceipt] = useState(false);
  const [generatingPdf, setGeneratingPdf] = useState(false);
  const [isAdmissionUpdating, setIsAdmissionUpdating] = useState(false);
  const [isReportUpdating, setIsReportUpdating] = useState(false);

  // ── Application Form modal state ──
  const [showAppFormModal, setShowAppFormModal] = useState(false);
  const [appFormStudent, setAppFormStudent] = useState(null);
  const [generatingAppFormPdf, setGeneratingAppFormPdf] = useState(false);
  const appFormRef = useRef(null);

  const getCurrentMonthDates = () => {
    const now = new Date();
    const firstDay = new Date(now.getFullYear(), now.getMonth(), 1);
    const lastDay = new Date(now.getFullYear(), now.getMonth() + 1, 0);
    const formatDate = (date) => {
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, "0");
      const day = String(date.getDate()).padStart(2, "0");
      return `${year}-${month}-${day}`;
    };
    return { fromDate: formatDate(firstDay), toDate: formatDate(lastDay) };
  };

  const displayValue = (value) => {
    if (value === null || value === undefined || value === "" || value === "-")
      return "N/A";
    return value;
  };

  const formatDMY = (value) => {
    if (!value) return "N/A";
    const d = new Date(value);
    if (isNaN(d.getTime())) return "N/A";
    return d
      .toLocaleDateString("en-GB", {
        day: "2-digit",
        month: "short",
        year: "numeric",
      })
      .replace(/ /g, "-");
  };

  useEffect(() => {
    const { fromDate: defaultFrom, toDate: defaultTo } = getCurrentMonthDates();
    setFromDate(defaultFrom);
    setToDate(defaultTo);
    fetchData(defaultFrom, defaultTo);
  }, []);

  const fetchData = async (from, to) => {
    setLoading(true);
    setError(null);
    try {
      const response = await fetch(SummaryApi.getallstudentsbydatefilter.url, {
        method: SummaryApi.getallstudentsbydatefilter.method,
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ from_date: from, to_date: to }),
      });
      if (response.ok) {
        const result = await response.json();
        if (result.status === true && Array.isArray(result.data)) {
          setStudents(result.data.map(mapStudent));
        } else {
          setStudents([]);
        }
      } else {
        throw new Error("Failed to fetch students");
      }
    } catch (err) {
      console.error("Fetch error:", err);
      setError("Failed to load student records. Please check connection.");
      setStudents([]);
    } finally {
      setLoading(false);
    }
  };

  const handleDateFilter = () => {
    if (!fromDate || !toDate) {
      toast.error("Please select both from and to dates");
      return;
    }
    setCurrentPage(1);
    fetchData(fromDate, toDate);
  };

  const handleExportExcel = () => {
    if (filteredStudents.length === 0) {
      toast.error("No data available to export");
      return;
    }
    const exportData = filteredStudents.map((student, index) => ({
      "S.No": index + 1,
      "Student Name": displayValue(student.student_name),
      "Father Name": displayValue(student.father_name),
      "Mother Name": displayValue(student.mother_name),
      "Date of Birth": student.date_of_birth
        ? new Date(student.date_of_birth)
            .toLocaleDateString("en-GB", {
              day: "2-digit",
              month: "short",
              year: "numeric",
            })
            .replace(/ /g, "-")
        : "N/A",
      "Caste": displayValue(student.caste),
      "Religion": displayValue(student.religion),
      Email: displayValue(student.email),
      Degree: displayValue(student.degree),
      Stream: displayValue(student.stream),
      "Academic Year": displayValue(student.academic_year),
      "Mobile Number": displayValue(student.mobile_number),
      "Parent Mobile Number": displayValue(student.alternate_mobile_number),
      Address: displayValue(student.address || student.address_line1),
      State: displayValue(student.state),
      District: displayValue(student.district),
      City: displayValue(student.city),
      Village: displayValue(student.village),
      Pincode: displayValue(student.pincode),
      "Aadhar Number": displayValue(student.aadhar),
      "Lead By": displayValue(student.lead_by),
      "UTR Number": displayValue(student.utr_number),
      "Transaction ID": displayValue(student.transaction_id),
      Amount: `₹${displayValue(student.amount)}`,
      "Payment Status": student.is_paid === "1" ? "Paid" : "Unpaid",
      "Admission Status": student.admission_done === "1" ? "Admitted" : "Not Admitted",
      "Reporting Status": student.is_reported === "1" ? "Reported" : "Not Reported",
      "Registered On": displayValue(student.registered_on),
      "Payment Date": student.payment_date
        ? new Date(student.payment_date).toLocaleString("en-IN")
        : "N/A",
    }));
    const worksheet = XLSX.utils.json_to_sheet(exportData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Student Report");
    const excelBuffer = XLSX.write(workbook, {
      bookType: "xlsx",
      type: "array",
    });
    const data = new Blob([excelBuffer], {
      type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;charset=UTF-8",
    });
    saveAs(data, `Student_Report_${Date.now()}.xlsx`);
  };

  const mapStudent = (item) => ({
    ...item,
    student_id: item.student_id || "N/A",
    student_name: item.student_name || "N/A",
    father_name: item.father_name || "N/A",
    mother_name: item.mother_name || "N/A",
    date_of_birth: item.date_of_birth || null,
    caste: item.caste || "N/A",
    religion: item.religion || "N/A",
    email: item.email || "N/A",
    degree: item.degree || "N/A",
    stream: item.stream || "N/A",
    academic_year: item.academic_year || "N/A",
    mobile_number: item.mobile_number || "N/A",
    alternate_mobile_number: item.alternate_mobile_number || "N/A",
    state: item.state || "N/A",
    district: item.district || "N/A",
    city: item.city || "N/A",
    village: item.village || "N/A",
    pincode: item.pincode || "N/A",
    aadhar: item.aadhar || "N/A",
    lead_by: item.lead_by || "N/A",
    utr_number: item.utr_number || "N/A",
    transaction_id: item.transaction_id || "N/A",
    payment_receipt: item.payment_receipt || "N/A",
    amount: item.amount || "0",
    is_paid: item.is_paid || "0",
    admission_done: item.admission_done || "0",
    is_reported: item.is_reported || "0",
    payment_date: item.payment_date || null,
    created_date_raw: item.created_date || null,
    registered_on: item.created_date
      ? new Date(item.created_date).toLocaleString("en-IN", {
          day: "2-digit",
          month: "2-digit",
          year: "numeric",
          hour: "2-digit",
          minute: "2-digit",
        })
      : "N/A",
    application_id: item.application_id || item.student_id || "N/A",
    nationality: item.nationality || "Indian",
    address_line1: item.address_line1 || item.address || "N/A",
    country: item.country || "India",
  });

  const filteredStudents = students.filter((student) => {
    const searchValue = enrollmentSearch
      .toLowerCase()
      .replace(/\s+/g, " ")
      .trim();
    const matchesSearch =
      searchValue === "" ||
      (student.student_name || "").toLowerCase().trim().includes(searchValue) ||
      (student.mobile_number || "")
        .toLowerCase()
        .trim()
        .includes(searchValue) ||
      (student.student_id || "").toLowerCase().trim().includes(searchValue) ||
      (student.lead_by || "").toLowerCase().trim().includes(searchValue);
    const matchesCourse =
      selectedCourse === "All" ||
      (student.stream || "").toLowerCase() === selectedCourse.toLowerCase();
    return matchesSearch && matchesCourse;
  });

  const totalPages = Math.ceil(filteredStudents.length / itemsPerPage);
  const currentStudents = filteredStudents.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage,
  );

  // One checkbox drives both actions:
  // - If student not yet admitted (and paid) -> counts toward Admission Done
  // - If student already admitted but not reported -> counts toward Report Done
  const selectableStudents = currentStudents.filter(
    (student) =>
      student.student_id &&
      student.student_id !== "N/A" &&
      ((student.admission_done !== "1" && student.is_paid === "1") ||
        (student.admission_done === "1" && student.is_reported !== "1"))
  );

  // Of the checked rows, which ones are actually pending admission
  const selectedForAdmission = selectedStudents.filter((id) => {
    const s = students.find((st) => st.student_id === id);
    return s && s.admission_done !== "1";
  });

  // Of the checked rows, which ones are already admitted and pending report
  const selectedForReport = selectedStudents.filter((id) => {
    const s = students.find((st) => st.student_id === id);
    return s && s.admission_done === "1" && s.is_reported !== "1";
  });

  // ─── Handle View Documents - Opens in new tab ────────────────────────────
  const handleViewDocuments = (student) => {
    if (!student.student_id || student.student_id === "N/A") {
      toast.error("Student ID missing");
      return;
    }
    const encodedId = btoa(student.student_id.toString());
    window.open(`/documents-checklist?id=${encodedId}`, "_blank");
  };

  const handleEditStudent = (studentId) => {
    if (!studentId || studentId === "N/A")
      return toast.error("Cannot edit: Student ID missing");
    const encodedId = btoa(studentId.toString());
    navigate(`/student-registration?id=${encodedId}`);
  };

  const handlePayNow = (student) => {
    if (!student.student_id || student.student_id === "N/A")
      return toast.error("Cannot process payment: Student ID missing");
    const encodedId = btoa(student.student_id.toString());
    const encodedName = btoa((student.student_name || "Unknown").trim());
    navigate(`/create-studentfee?id=${encodedId}&name=${encodedName}`);
  };

  const handleViewMore = (student) => {
    setSelectedStudent(student);
    setReceiptImageUrl(null);
    setShowModal(true);
    fetchReceiptImage(student.student_id);
  };

  const closeModal = () => {
    setShowModal(false);
    setSelectedStudent(null);
    setReceiptImageUrl(null);
    setLoadingReceipt(false);
  };

  const fetchReceiptImage = async (studentId) => {
    if (!studentId || studentId === "N/A") {
      setReceiptImageUrl(null);
      setLoadingReceipt(false);
      return;
    }
    setLoadingReceipt(true);
    try {
      const response = await fetch(SummaryApi.getallstudentsbydatefilter.url, {
        method: SummaryApi.getallstudentsbydatefilter.method,
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ from_date: fromDate, to_date: toDate }),
      });
      if (response.ok) {
        const result = await response.json();
        if (result.status === true && Array.isArray(result.data)) {
          const student = result.data.find((s) => s.student_id === studentId);
          if (
            student &&
            student.payment_receipt &&
            student.payment_receipt !== "N/A"
          ) {
            const receiptPath = student.payment_receipt;
            const filename = receiptPath.split("/").pop();
            const baseUrl = SummaryApi.downloadfiles?.url || "";
            const fullImageUrl = `${baseUrl}?student_id=${studentId}&file=${filename}`;
            setReceiptImageUrl(fullImageUrl);
          } else {
            setReceiptImageUrl(null);
          }
        } else {
          setReceiptImageUrl(null);
        }
      } else {
        setReceiptImageUrl(null);
      }
    } catch (err) {
      console.error("Fetch receipt error:", err);
      setReceiptImageUrl(null);
    } finally {
      setLoadingReceipt(false);
    }
  };

  const handleViewReceipt = () => {
    if (receiptImageUrl) {
      window.open(receiptImageUrl, "_blank");
    } else {
      toast.error("No receipt found for this student");
    }
  };

  const handleDownloadReceipt = async () => {
    if (!receiptImageUrl) {
      toast.error("No receipt found for this student");
      return;
    }
    try {
      const response = await fetch(receiptImageUrl, {
        method: "GET",
        headers: { Accept: "image/*" },
      });
      if (!response.ok)
        throw new Error(`Failed to fetch receipt: ${response.status}`);
      const blob = await response.blob();
      if (!blob.type.startsWith("image/")) {
        toast.error("Invalid receipt format");
        return;
      }
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `Receipt_${selectedStudent?.student_name || "student"}_${selectedStudent?.student_id || "unknown"}.${blob.type.split("/")[1] || "png"}`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      toast.success("Downloading receipt...");
    } catch (err) {
      console.error("Download receipt error:", err);
      toast.error("Failed to download receipt. Please try again.");
    }
  };

  // ─── Generate PDF Receipt in payment confirmation format ───────────────────
  const handleDownloadReceiptPDF = async () => {
    if (!selectedStudent) return;
    if (selectedStudent.is_paid !== "1") {
      toast.error("Receipt is only available for paid students");
      return;
    }

    setGeneratingPdf(true);
    try {
      const doc = new jsPDF({ unit: "pt", format: "a4" });
      const pageWidth = doc.internal.pageSize.getWidth();
      const pageHeight = doc.internal.pageSize.getHeight();
      const margin = 40;
      const cardX = margin;
      const cardY = 28;
      const cardW = pageWidth - margin * 2;

      // Background
      doc.setFillColor(242, 242, 242);
      doc.rect(0, 0, pageWidth, pageHeight, "F");

      // White card
      doc.setFillColor(255, 255, 255);
      doc.roundedRect(cardX, cardY, cardW, 580, 8, 8, "F");

      // Top grey strip
      doc.setFillColor(245, 245, 245);
      doc.rect(cardX, cardY, cardW, 18, "F");

      let y = cardY + 35;

      // Logo
      try {
        const img = new Image();
        img.src = ghrLogo;
        await new Promise((resolve, reject) => {
          img.onload = resolve;
          img.onerror = reject;
        });
        const logoCanvas = document.createElement("canvas");
        logoCanvas.width = img.width;
        logoCanvas.height = img.height;
        const ctx = logoCanvas.getContext("2d");
        ctx.drawImage(img, 0, 0);
        const logoDataUrl = logoCanvas.toDataURL("image/png");
        const logoH = 45;
        const logoW = (img.width / img.height) * logoH;
        doc.addImage(logoDataUrl, "PNG", margin + 10, y - 8, logoW, logoH);
      } catch (_) {
        /* skip logo if unavailable */
      }

      // Dynamic Amount
      const paymentAmount = selectedStudent.amount || "0";
      doc.setFont("helvetica", "bold");
      doc.setFontSize(18);
      doc.setTextColor(17, 17, 17);
      const amountText = `${paymentAmount}`;
      const rsText = "RS";
      doc.setFont("helvetica", "bold");
      doc.setFontSize(18);
      const amountWidth = doc.getTextWidth(amountText);
      const rsWidth = doc.getTextWidth(rsText);
      const startX = pageWidth - margin - 10 - amountWidth - rsWidth + 8;
      doc.text(amountText, startX, y + 10);
      doc.setFontSize(10);
      doc.text("RS", startX + amountWidth + 4, y + 8);
      doc.setFont("helvetica", "normal");
      doc.setFontSize(11);
      doc.setTextColor(119, 119, 119);
      doc.text("via UPI", pageWidth - margin - 10, y + 28, { align: "right" });

      y += 80;

      // Brand
      doc.setFont("times", "bold");
      doc.setFontSize(32);
      doc.setTextColor(229, 57, 53);
      doc.text("raisoni", margin + 12, y);

      y += 5;
      doc.setFont("helvetica", "bold");
      doc.setFontSize(10);
      doc.setCharSpace(3);
      doc.setTextColor(229, 57, 53);
      doc.text("EDUCATION", margin + 12, y + 8);
      doc.setCharSpace(0);

      y += 25;

      // Success message
      doc.setFont("helvetica", "italic");
      doc.setFontSize(16);
      doc.setTextColor(46, 125, 50);
      doc.text("Your payment successfully completed.", margin + 12, y);

      y += 22;

      // Message box
      const boxX = margin + 10;
      const boxW = cardW - 20;
      const studentName = displayValue(selectedStudent.student_name);
      const studentEmail = displayValue(selectedStudent.email);

      doc.setDrawColor(221, 221, 221);
      doc.setFillColor(250, 250, 250);
      doc.roundedRect(boxX, y, boxW, 85, 5, 5, "FD");

      y += 18;
      doc.setFont("helvetica", "normal");
      doc.setFontSize(13);
      doc.setTextColor(51, 51, 51);
      const hiW = doc.getTextWidth("Hi ");
      doc.text("Hi ", boxX + 14, y);
      doc.setFont("helvetica", "bold");
      doc.text(`${studentName},`, boxX + 14 + hiW, y);

      y += 18;
      doc.setFont("helvetica", "normal");
      doc.setFontSize(12);
      doc.setTextColor(51, 51, 51);
      doc.text("Thanks for using RAISONI!", boxX + 14, y);

      y += 16;
      doc.setFontSize(11);
      doc.text(
        "For any further queries you can get in touch with the merchant on",
        boxX + 14,
        y,
      );

      y += 14;
      doc.setTextColor(26, 115, 232);
      doc.text(studentEmail, boxX + 14, y);

      y += 32;

      // Details grid
      const col1 = margin + 12;
      const col2 = margin + 12 + cardW / 2;
      const labelClr = [119, 119, 119];
      const valClr = [34, 34, 34];

      // Row 1
      doc.setFont("helvetica", "normal");
      doc.setFontSize(10);
      doc.setTextColor(...labelClr);
      doc.text("Transaction ID:", col1, y);
      doc.text("Email :", col2, y);

      y += 14;
      doc.setFont("helvetica", "bold");
      doc.setFontSize(12);
      doc.setTextColor(...valClr);
      doc.text(displayValue(selectedStudent.transaction_id), col1, y);
      doc.setTextColor(26, 115, 232);
      doc.text(studentEmail, col2, y);

      y += 24;

      // Row 2
      doc.setFont("helvetica", "normal");
      doc.setFontSize(10);
      doc.setTextColor(...labelClr);
      doc.text("Contact Number:", col1, y);
      doc.text("UTR Number:", col2, y);

      y += 14;
      doc.setFont("helvetica", "bold");
      doc.setFontSize(12);
      doc.setTextColor(...valClr);
      doc.text(displayValue(selectedStudent.mobile_number), col1, y);
      doc.text(displayValue(selectedStudent.utr_number), col2, y);

      y += 24;

      // Row 3
      doc.setFont("helvetica", "normal");
      doc.setFontSize(10);
      doc.setTextColor(...labelClr);
      doc.text("Paid Amount:", col1, y);
      doc.text("Payment Date:", col2, y);

      y += 14;
      doc.setFont("helvetica", "bold");
      doc.setFontSize(12);
      doc.setTextColor(...valClr);
      const paidAmount = `${paymentAmount}`;
      doc.setFont("helvetica", "bold");
      doc.setFontSize(12);
      doc.text(paidAmount, col1, y);
      const paidWidth = doc.getTextWidth(paidAmount);
      doc.setFontSize(8);
      doc.text("RS", col1 + paidWidth + 4, y - 2);
      doc.setFont("helvetica", "bold");
      doc.setFontSize(14);
      doc.text(
        selectedStudent.payment_date
          ? new Date(selectedStudent.payment_date).toLocaleString("en-IN")
          : "N/A",
        col2,
        y,
      );

      y += 30;

      // Divider
      doc.setDrawColor(229, 229, 229);
      doc.setLineWidth(0.8);
      doc.line(margin + 12, y, pageWidth - margin - 12, y);

      doc.save(
        `Receipt_${studentName}_${selectedStudent.student_id || "unknown"}.pdf`,
      );
      toast.success("Receipt PDF downloaded successfully!");
    } catch (err) {
      console.error("PDF generation error:", err);
      toast.error("Failed to generate PDF receipt. Please try again.");
    } finally {
      setGeneratingPdf(false);
    }
  };

  // ─── Application Form Modal handlers ───────────────────────────────────────
  const handleViewApplicationForm = (student) => {
    setAppFormStudent(student);
    setShowAppFormModal(true);
  };

  const closeAppFormModal = () => {
    setShowAppFormModal(false);
    setAppFormStudent(null);
  };

  // Print only the application-form content
  const handlePrintApplicationForm = () => {
    window.print();
  };

  // Capture the exact on-screen application form and export as PDF
  const handleDownloadApplicationFormPDF = async () => {
    if (!appFormRef.current || !appFormStudent) return;
    setGeneratingAppFormPdf(true);
    try {
      const canvas = await html2canvas(appFormRef.current, {
        scale: 2,
        useCORS: true,
        backgroundColor: "#ffffff",
      });

      const imgData = canvas.toDataURL("image/png");
      const pdf = new jsPDF({ unit: "pt", format: "a4" });
      const pageWidth = pdf.internal.pageSize.getWidth();
      const pageHeight = pdf.internal.pageSize.getHeight();

      const imgWidth = pageWidth;
      const imgHeight = (canvas.height * imgWidth) / canvas.width;

      let heightLeft = imgHeight;
      let position = 0;

      pdf.addImage(imgData, "PNG", 0, position, imgWidth, imgHeight);
      heightLeft -= pageHeight;

      while (heightLeft > 0) {
        position = heightLeft - imgHeight;
        pdf.addPage();
        pdf.addImage(imgData, "PNG", 0, position, imgWidth, imgHeight);
        heightLeft -= pageHeight;
      }

      pdf.save(
        `Application_Form_${appFormStudent.student_name || "student"}_${appFormStudent.student_id || "unknown"}.pdf`,
      );
      toast.success("Application form PDF downloaded successfully!");
    } catch (err) {
      console.error("Application form PDF generation error:", err);
      toast.error("Failed to generate application form PDF. Please try again.");
    } finally {
      setGeneratingAppFormPdf(false);
    }
  };
const clearAllFilters = () => {
  const { fromDate: defaultFrom, toDate: defaultTo } = getCurrentMonthDates();

  setEnrollmentSearch("");
  setSelectedCourse("All");
  setFromDate(defaultFrom);
  setToDate(defaultTo);
  setCurrentPage(1);

  // Reload current month's data
  fetchData(defaultFrom, defaultTo);
};

  const getUniqueCourses = () => {
    const courses = students.map((s) => s.stream).filter(Boolean);
    return [...new Set(courses)].sort();
  };

  // Single checkbox: works for BOTH Admission Done and Report Done.
  // - Not admitted + paid -> selecting it counts toward Admission Done
  // - Admitted + not reported -> selecting it counts toward Report Done
  const handleStudentSelect = (studentId) => {
    const student = students.find(s => s.student_id === studentId);

    if (!student) return;

    // Already admitted -> this checkbox now targets Reporting
    if (student.admission_done === "1") {
      if (student.is_reported === "1") {
        toast.info(
          `ℹ️ ${student.student_name || "Student"} is already reported.`,
          { duration: 3000 }
        );
        return;
      }
      setSelectedStudents((prev) =>
        prev.includes(studentId)
          ? prev.filter((id) => id !== studentId)
          : [...prev, studentId]
      );
      return;
    }

    // Not admitted yet -> this checkbox targets Admission (requires payment)
    if (student.is_paid !== "1") {
      toast.error(
        `⚠️ Payment Required: ${student.student_name || "Student"} has not completed the payment. Please process the payment before admission.`,
        { duration: 5000 }
      );
      return;
    }

    setSelectedStudents((prev) => {
      if (prev.includes(studentId)) {
        return prev.filter((id) => id !== studentId);
      } else {
        return [...prev, studentId];
      }
    });
  };

  // Selects every eligible row on the page (pending-admission or pending-report)
  const handleSelectAll = () => {
    if (selectAll) {
      setSelectedStudents([]);
    } else {
      const selectableIds = selectableStudents
        .map((student) => student.student_id)
        .filter((id) => id && id !== "N/A");

      if (selectableIds.length === 0) {
        const unpaidCount = currentStudents.filter(s => s.is_paid !== "1" && s.admission_done !== "1").length;
        if (unpaidCount > 0) {
          toast.error(
            `⚠️ Cannot select students. ${unpaidCount} student(s) have pending payments. Please complete the payment first.`,
            { duration: 5000 }
          );
        } else {
          toast.info("ℹ️ No selectable students available on this page.", { duration: 3000 });
        }
        return;
      }

      setSelectedStudents(selectableIds);
    }
    setSelectAll(!selectAll);
  };

  // ─── Admission Done function - acts only on the admission-pending rows ──
  // Uses the SAME checkbox selection as Report Done; only the rows that are
  // not yet admitted (selectedForAdmission) are sent here.
  const handleAdmissionDone = async () => {
    if (selectedForAdmission.length === 0) {
      toast.error("Please select at least one student for admission");
      return;
    }

    // Show confirmation toast with action buttons
    toast.custom((t) => (
      <div className={`${t.visible ? 'animate-enter' : 'animate-leave'} max-w-md w-full bg-white shadow-lg rounded-lg pointer-events-auto flex ring-1 ring-black ring-opacity-5`}>
        <div className="flex-1 p-4">
          <div className="flex items-start">
            <div className="flex-shrink-0 pt-0.5">
              <AlertCircle className="h-6 w-6 text-amber-500" />
            </div>
            <div className="ml-3 flex-1">
              <p className="text-sm font-medium text-gray-900">
                Confirm Admission
              </p>
              <p className="text-sm text-gray-500">
                Are you sure you want to process admission for <strong>{selectedForAdmission.length}</strong> student(s)?
              </p>
            </div>
          </div>
        </div>
        <div className="flex border-l border-gray-200">
          <button
            onClick={() => {
              toast.dismiss(t.id);
              processAdmission();
            }}
            className="w-full border border-transparent rounded-none rounded-r-lg p-4 flex items-center justify-center text-sm font-medium text-indigo-600 hover:text-indigo-500 hover:bg-indigo-50 focus:outline-none"
          >
            Confirm
          </button>
          <button
            onClick={() => toast.dismiss(t.id)}
            className="w-full border border-transparent rounded-none p-4 flex items-center justify-center text-sm font-medium text-gray-700 hover:text-gray-500 hover:bg-gray-50 focus:outline-none"
          >
            Cancel
          </button>
        </div>
      </div>
    ), { duration: 10000 });

    const processAdmission = async () => {
      setIsAdmissionUpdating(true);
      let successCount = 0;
      let failCount = 0;
      const succeededIds = [];
      const failedStudents = [];

      try {
        // Process only the admission-pending rows from the shared selection
        for (const studentId of selectedForAdmission) {
          try {
            const response = await fetch(SummaryApi.studentadmissionupdate.url, {
              method: SummaryApi.studentadmissionupdate.method,
              headers: {
                "Content-Type": "application/json",
              },
              body: JSON.stringify({
                student_id: studentId,
                admission_done: "1",
              }),
            });

            const data = await response.json();

            if (response.ok && data.status === true) {
              successCount++;
              succeededIds.push(studentId);
            } else {
              failCount++;
              failedStudents.push(studentId);
              console.error(`Failed to update student ${studentId}:`, data.message || "Unknown error");
            }
          } catch (error) {
            failCount++;
            failedStudents.push(studentId);
            console.error(`Error updating student ${studentId}:`, error);
          }
        }

        // Immediately reflect the change in the table without refetching/reloading
        if (succeededIds.length > 0) {
          setStudents((prev) =>
            prev.map((s) =>
              succeededIds.includes(s.student_id)
                ? { ...s, admission_done: "1" }
                : s
            )
          );
        }

        // Show summary toast
        if (successCount > 0 && failCount === 0) {
          toast.success(`Admission completed successfully for ${successCount} student(s)!`);
        } else if (successCount > 0 && failCount > 0) {
          toast.warning(`Partially completed: ${successCount} succeeded, ${failCount} failed`);
        } else if (failCount > 0 && successCount === 0) {
          toast.error(`Failed to process admission for ${failCount} student(s)`);
        }

        // Only clear the admission ids we just processed - leave any
        // report-pending selections in place so the same checkbox keeps
        // reflecting the row's new state (now eligible for Report Done).
        setSelectedStudents((prev) => prev.filter((id) => !succeededIds.includes(id)));
        setSelectAll(false);

      } catch (error) {
        console.error("Admission update error:", error);
        toast.error("Failed to process admission. Please try again.");
      } finally {
        setIsAdmissionUpdating(false);
      }
    };
  };

  // ─── Report Done function - uses the SAME checkbox selection as Admission ─
  // Only the rows that are already admitted but not yet reported
  // (selectedForReport) are sent to the reporting API.
  const handleReportDone = async () => {
    if (selectedForReport.length === 0) {
      toast.error("Please select at least one admitted student to mark as reported");
      return;
    }

    // Show confirmation toast with action buttons
    toast.custom((t) => (
      <div className={`${t.visible ? 'animate-enter' : 'animate-leave'} max-w-md w-full bg-white shadow-lg rounded-lg pointer-events-auto flex ring-1 ring-black ring-opacity-5`}>
        <div className="flex-1 p-4">
          <div className="flex items-start">
            <div className="flex-shrink-0 pt-0.5">
              <AlertCircle className="h-6 w-6 text-amber-500" />
            </div>
            <div className="ml-3 flex-1">
              <p className="text-sm font-medium text-gray-900">
                Confirm Reporting
              </p>
              <p className="text-sm text-gray-500">
                Are you sure you want to mark <strong>{selectedForReport.length}</strong> student(s) as reported?
              </p>
            </div>
          </div>
        </div>
        <div className="flex border-l border-gray-200">
          <button
            onClick={() => {
              toast.dismiss(t.id);
              processReporting();
            }}
            className="w-full border border-transparent rounded-none rounded-r-lg p-4 flex items-center justify-center text-sm font-medium text-teal-600 hover:text-teal-500 hover:bg-teal-50 focus:outline-none"
          >
            Confirm
          </button>
          <button
            onClick={() => toast.dismiss(t.id)}
            className="w-full border border-transparent rounded-none p-4 flex items-center justify-center text-sm font-medium text-gray-700 hover:text-gray-500 hover:bg-gray-50 focus:outline-none"
          >
            Cancel
          </button>
        </div>
      </div>
    ), { duration: 10000 });

    const processReporting = async () => {
      setIsReportUpdating(true);
      let successCount = 0;
      let failCount = 0;
      const succeededIds = [];
      const failedStudents = [];

      try {
        // Process only the report-pending rows from the shared selection
        for (const studentId of selectedForReport) {
          try {
            const response = await fetch(SummaryApi.updatereportingstudents.url, {
              method: SummaryApi.updatereportingstudents.method,
              headers: {
                "Content-Type": "application/json",
              },
              body: JSON.stringify({
                student_id: studentId,
                is_reported: "1",
              }),
            });

            const data = await response.json();

            if (response.ok && data.status === true) {
              successCount++;
              succeededIds.push(studentId);
            } else {
              failCount++;
              failedStudents.push(studentId);
              console.error(`Failed to update reporting for student ${studentId}:`, data.message || "Unknown error");
            }
          } catch (error) {
            failCount++;
            failedStudents.push(studentId);
            console.error(`Error updating reporting for student ${studentId}:`, error);
          }
        }

        // Immediately reflect the change in the table without refetching/reloading
        if (succeededIds.length > 0) {
          setStudents((prev) =>
            prev.map((s) =>
              succeededIds.includes(s.student_id)
                ? { ...s, is_reported: "1" }
                : s
            )
          );
        }

        // Show summary toast
        if (successCount > 0 && failCount === 0) {
          toast.success(`Reporting completed successfully for ${successCount} student(s)!`);
        } else if (successCount > 0 && failCount > 0) {
          toast.warning(`Partially completed: ${successCount} succeeded, ${failCount} failed`);
        } else if (failCount > 0 && successCount === 0) {
          toast.error(`Failed to update reporting for ${failCount} student(s)`);
        }

        // Clear only the processed ids from the shared selection
        setSelectedStudents((prev) => prev.filter((id) => !succeededIds.includes(id)));
        setSelectAll(false);

      } catch (error) {
        console.error("Reporting update error:", error);
        toast.error("Failed to process reporting. Please try again.");
      } finally {
        setIsReportUpdating(false);
      }
    };
  };

  // ─── Function to render admission status badge ───────────────────────────
  const renderAdmissionStatus = (admissionDone) => {
    if (admissionDone === "1") {
      return (
        <span className="inline-flex items-center gap-1 px-1.5 py-0.5 text-[10px] font-medium rounded-full bg-green-100 text-green-800">
          <CheckCircle size={10} />
          Admitted
        </span>
      );
    } else {
      return (
        <span className="inline-flex items-center gap-1 px-1.5 py-0.5 text-[10px] font-medium rounded-full bg-yellow-100 text-yellow-800">
          <XCircle size={10} />
          Not Admitted
        </span>
      );
    }
  };

  // ─── Function to render reporting status badge ───────────────────────────
  const renderReportStatus = (isReported) => {
    if (isReported === "1") {
      return (
        <span className="inline-flex items-center gap-1 px-1.5 py-0.5 text-[10px] font-medium rounded-full bg-green-100 text-green-800">
          <CheckCircle size={10} />
          Reported
        </span>
      );
    } else {
      return (
        <span className="inline-flex items-center gap-1 px-1.5 py-0.5 text-[10px] font-medium rounded-full bg-yellow-100 text-yellow-800">
          <XCircle size={10} />
          Not Reported
        </span>
      );
    }
  };

  // ─── Single checkbox disable logic (drives Admission Done + Report Done) ─
  // Disabled when: invalid ID, OR not admitted yet and unpaid, OR already
  // admitted and already reported (nothing left to do for this row).
  const isCheckboxDisabled = (student) => {
    if (!student.student_id || student.student_id === "N/A") return true;
    if (student.admission_done === "1") {
      return student.is_reported === "1";
    }
    return student.is_paid !== "1";
  };

  // ─── Tooltip explaining why the shared checkbox is disabled/enabled ─────
  const getDisabledReason = (student) => {
    if (!student.student_id || student.student_id === "N/A") {
      return "Invalid student ID";
    }
    if (student.admission_done === "1") {
      return student.is_reported === "1"
        ? "Student is already admitted and reported"
        : "Select to mark as Reported";
    }
    if (student.is_paid !== "1") {
      return `⚠️ Payment pending for ${student.student_name || "this student"}. Please complete payment first.`;
    }
    return "Select to mark as Admitted";
  };

  return (
    <div className="max-w-[98%] mx-auto px-2 py-3">
      {/* Print styles */}
      <style>{`
        @media print {
          body * { visibility: hidden; }
          #application-form-print, #application-form-print * { visibility: visible; }
          #application-form-print {
            position: absolute;
            left: 0;
            top: 0;
            width: 100%;
            background: white !important;
          }
          .no-print { display: none !important; }
          .app-form-header {
            background: #3b1a6b !important;
            -webkit-print-color-adjust: exact !important;
            print-color-adjust: exact !important;
          }
          .app-form-subheader {
            background: #4e2280 !important;
            -webkit-print-color-adjust: exact !important;
            print-color-adjust: exact !important;
          }
          .app-form-gold-text {
            color: #f5d87a !important;
            -webkit-print-color-adjust: exact !important;
            print-color-adjust: exact !important;
          }
        }
      `}</style>

      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-3 gap-2">
        <h2 className="text-lg font-bold text-gray-800">
          Student Enrollment &amp; Billing Report
        </h2>
        <div className="flex gap-2">
          <button
            onClick={handleAdmissionDone}
            disabled={isAdmissionUpdating || selectedForAdmission.length === 0}
            className="bg-indigo-600 hover:bg-indigo-700 disabled:bg-indigo-400 disabled:cursor-not-allowed text-white px-3 py-1.5 rounded-md text-xs font-medium transition flex items-center gap-1.5"
          >
            {isAdmissionUpdating ? (
              <>
                <span className="animate-spin rounded-full h-3 w-3 border-2 border-white border-t-transparent"></span>
                Processing...
              </>
            ) : (
              <>
                <span>Admission Done</span>
                {selectedForAdmission.length > 0 && (
                  <span className="bg-white text-indigo-600 px-1.5 py-0.5 rounded-full text-[10px] font-bold">
                    {selectedForAdmission.length}
                  </span>
                )}
              </>
            )}
          </button>
          <button
            onClick={handleReportDone}
            disabled={isReportUpdating || selectedForReport.length === 0}
            className="bg-teal-600 hover:bg-teal-700 disabled:bg-teal-400 disabled:cursor-not-allowed text-white px-3 py-1.5 rounded-md text-xs font-medium transition flex items-center gap-1.5"
          >
            {isReportUpdating ? (
              <>
                <span className="animate-spin rounded-full h-3 w-3 border-2 border-white border-t-transparent"></span>
                Processing...
              </>
            ) : (
              <>
                <span>Report Done</span>
                {selectedForReport.length > 0 && (
                  <span className="bg-white text-teal-600 px-1.5 py-0.5 rounded-full text-[10px] font-bold">
                    {selectedForReport.length}
                  </span>
                )}
              </>
            )}
          </button>
          <button
            onClick={handleExportExcel}
            className="bg-green-600 hover:bg-green-700 text-white px-3 py-1.5 rounded-md text-xs font-medium transition"
          >
            Export Excel
          </button>
        </div>
      </div>

      {/* Filters */}
      <div className="mb-3 flex flex-col sm:flex-row flex-wrap gap-2 items-end">
        <div className="flex-1 min-w-[280px]">
          <input
            type="text"
            placeholder="Search by Student Name, Mobile No, Lead By..."
            value={enrollmentSearch}
            onChange={(e) => {
              setEnrollmentSearch(e.target.value);
              setCurrentPage(1);
            }}
            className="w-full px-3 py-1.5 text-sm border border-gray-300 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          />
        </div>

        <div className="flex flex-wrap gap-2">
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-0.5">
              From Date
            </label>
            <input
              type="date"
              value={fromDate}
              onChange={(e) => setFromDate(e.target.value)}
              className="px-2 py-1.5 text-sm border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-0.5">
              To Date
            </label>
            <input
              type="date"
              value={toDate}
              onChange={(e) => setToDate(e.target.value)}
              className="px-2 py-1.5 text-sm border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-0.5">
              Apply Date Filter
            </label>
            <button
              onClick={handleDateFilter}
              className="px-3 py-1.5 text-sm bg-blue-600 hover:bg-blue-700 text-white rounded-md font-medium transition w-full"
            >
              Apply
            </button>
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-0.5">
              Stream/Course
            </label>
            <select
              value={selectedCourse}
              onChange={(e) => {
                setSelectedCourse(e.target.value);
                setCurrentPage(1);
              }}
              className="px-2 py-1.5 text-sm border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 min-w-[200px]"
            >
              <option value="All">All Courses</option>
              {getUniqueCourses().map((course) => (
                <option key={course} value={course}>
                  {course}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-0.5">
              {" "}
            </label>
            <button
              onClick={clearAllFilters}
              className="px-3 py-1.5 text-sm mt-1 bg-gray-200 hover:bg-gray-300 text-gray-800 rounded-md font-medium transition"
            >
              Clear Filters
            </button>
          </div>
        </div>
      </div>

      {loading && (
        <div className="text-center py-8 text-sm font-medium text-gray-700">
          Loading student records...
        </div>
      )}
      {error && (
        <div className="mb-3 p-3 text-sm bg-red-50 border border-red-200 text-red-700 rounded-lg">
          {error}
        </div>
      )}

      {!loading && !error && (
        <>
          <div className="overflow-x-auto bg-white shadow-lg rounded-xl border border-gray-200">
            <table className="min-w-full divide-y divide-gray-200 text-xs">
              <thead className="bg-gradient-to-r from-gray-50 to-gray-100">
                <tr>
                  <th className="px-2 py-2 text-center w-10">
                    <input
                      type="checkbox"
                      checked={selectAll && selectedStudents.length > 0}
                      onChange={handleSelectAll}
                      className="w-3.5 h-3.5 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                      disabled={selectableStudents.length === 0}
                      title={selectableStudents.length === 0 ? "No selectable students available" : "Select for Admission / Reporting"}
                    />
                    <span className="block text-[8px] text-gray-500 mt-0.5">Select</span>
                  </th>
                  <th className="px-2 py-2 text-left font-semibold text-gray-800 w-10">
                    S.No
                  </th>
                  <th className="px-2 py-2 text-left font-semibold text-gray-800">
                    Student Name
                  </th>
                  <th className="px-2 py-2 text-left font-semibold text-gray-800">
                    Father Name
                  </th>
                  <th className="px-2 py-2 text-left font-semibold text-gray-800">
                    Mobile
                  </th>
                  <th className="px-2 py-2 text-left font-semibold text-gray-800">
                    Stream
                  </th>
                  <th className="px-2 py-2 text-left font-semibold text-gray-800">
                    Lead By
                  </th>
                  <th className="px-2 py-2 text-left font-semibold text-gray-800">
                    Registered On
                  </th>
                  <th className="px-2 py-2 text-left font-semibold text-gray-800">
                    Payment Status
                  </th>
                  <th className="px-2 py-2 text-left font-semibold text-gray-800">
                    Admission Status
                  </th>
                  <th className="px-2 py-2 text-left font-semibold text-gray-800">
                    Reporting Status
                  </th>
                  <th className="px-2 py-2 text-center font-semibold text-gray-800 w-28">
                    Application Form
                  </th>
                  {/* NEW: Documents Column */}
                  <th className="px-2 py-2 text-center font-semibold text-gray-800 w-24">
                    Documents
                  </th>
                  <th className="px-2 py-2 text-center font-semibold text-gray-800 w-24">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100 bg-white">
                {currentStudents.map((student, index) => {
                  const isDisabled = isCheckboxDisabled(student);
                  const disabledReason = getDisabledReason(student);
                  // Same checkbox, but its "purpose" depends on the row's current state
                  const targetsReport = student.admission_done === "1";

                  return (
                    <tr
                      key={student.student_id ? `${student.student_id}-${index}` : `row-${index}`}
                      className={`hover:bg-blue-50 transition-colors ${student.is_paid !== "1" && student.admission_done !== "1" ? "opacity-75" : ""}`}
                    >
                      <td className="px-2 py-1.5 text-center">
                        <input
                          type="checkbox"
                          checked={selectedStudents.includes(student.student_id)}
                          onChange={() => handleStudentSelect(student.student_id)}
                          className={`w-3.5 h-3.5 border-gray-300 rounded ${targetsReport ? "text-teal-600 focus:ring-teal-500" : "text-blue-600 focus:ring-blue-500"}`}
                          disabled={isDisabled}
                          title={isDisabled ? disabledReason : (targetsReport ? "Select to mark as Reported" : "Select to mark as Admitted")}
                        />
                        {isDisabled && student.is_paid !== "1" && student.admission_done !== "1" && (
                          <span className="block text-[8px] text-red-500 mt-0.5">Pending</span>
                        )}
                      </td>
                      <td className="px-2 py-1.5 text-center font-medium text-gray-900">
                        {(currentPage - 1) * itemsPerPage + index + 1}
                      </td>
                      <td className="px-2 py-1.5 font-medium text-gray-900">
                        {displayValue(student.student_name)}
                      </td>
                      <td className="px-2 py-1.5">
                        {displayValue(student.father_name)}
                      </td>
                      <td className="px-2 py-1.5">
                        {displayValue(student.mobile_number)}
                      </td>
                      <td className="px-2 py-1.5">
                        {displayValue(student.stream)}
                      </td>
                      <td className="px-2 py-1.5">
                        <span className="inline-block px-1.5 py-0.5 text-[10px] font-medium rounded-full bg-blue-50 text-blue-700 whitespace-nowrap">
                          {displayValue(student.lead_by)}
                        </span>
                      </td>
                      <td className="px-2 py-1.5">
                        {displayValue(student.registered_on)}
                      </td>
                      <td className="px-2 py-1.5">
                        <span
                          className={`inline-block px-1.5 py-0.5 text-[10px] font-medium rounded-full ${student.is_paid === "1" ? "bg-green-100 text-green-800" : "bg-red-100 text-red-800"}`}
                        >
                          {student.is_paid === "1" ? "Paid" : "Unpaid"}
                        </span>
                      </td>
                      <td className="px-2 py-1.5">
                        {renderAdmissionStatus(student.admission_done)}
                      </td>
                      <td className="px-2 py-1.5">
                        {renderReportStatus(student.is_reported)}
                      </td>
                      <td className="px-2 py-1.5 text-center">
                        <button
                          onClick={() => handleViewApplicationForm(student)}
                          className="bg-indigo-600 hover:bg-indigo-700 text-white px-2 py-1 rounded-md text-[11px] font-medium transition flex items-center justify-center gap-1 mx-auto"
                        >
                          <FileText size={12} /> View Form
                        </button>
                      </td>
                      {/* NEW: Documents Column Button */}
                      <td className="px-2 py-1.5 text-center">
                        <button
                          onClick={() => handleViewDocuments(student)}
                          className="bg-purple-600 hover:bg-purple-700 text-white px-2 py-1 rounded-md text-[11px] font-medium transition flex items-center justify-center gap-1 mx-auto"
                        >
                          <FolderOpen size={11} /> View Docs
                        </button>
                      </td>
                      <td className="px-2 py-1.5 text-center">
                        <button
                          onClick={() => handleViewMore(student)}
                          className="bg-blue-600 hover:bg-blue-700 text-white px-2 py-1 rounded-md text-[11px] font-medium transition flex items-center justify-center gap-1 mx-auto"
                        >
                          <Eye size={12} /> View
                        </button>
                        {(!student.is_paid || student.is_paid === "0" || student.is_paid === 0) && (
                          <button
                            onClick={() => handlePayNow(student)}
                            className="bg-yellow-600 hover:bg-yellow-700 text-white px-2 py-1 mt-1 rounded-md text-[11px] font-medium transition w-full"
                          >
                            Pay Now
                          </button>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          <div className="flex flex-col sm:flex-row justify-between items-center mt-3 gap-2">
            <button
              onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
              disabled={currentPage === 1 || currentStudents.length === 0}
              className="bg-gray-200 hover:bg-gray-300 text-gray-800 px-4 py-1.5 text-sm rounded-md font-medium disabled:opacity-50 transition"
            >
              Previous
            </button>
            <span className="text-gray-700 text-sm font-medium">
              Page {currentPage} of {totalPages || 1} ({filteredStudents.length}{" "}
              records)
            </span>
            <button
              onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
              disabled={
                currentPage === totalPages || currentStudents.length === 0
              }
              className="bg-gray-200 hover:bg-gray-300 text-gray-800 px-4 py-1.5 text-sm rounded-md font-medium disabled:opacity-50 transition"
            >
              Next
            </button>
          </div>
        </>
      )}

      {/* ── Student Details Modal ── */}
      {showModal && selectedStudent && (
        <div className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-2xl max-w-5xl w-full max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 bg-white border-b px-6 py-4 flex justify-between items-center z-10">
              <h3 className="text-xl font-bold text-gray-800">
                Student Details - {displayValue(selectedStudent.student_name)}
              </h3>

              <div className="flex items-center gap-3">
                {selectedStudent.is_paid === "1" && (
                  <button
                    onClick={handleDownloadReceiptPDF}
                    disabled={generatingPdf}
                    className="flex items-center gap-1.5 bg-purple-600 hover:bg-purple-700 disabled:bg-purple-400 text-white px-4 py-1.5 rounded-md text-sm font-medium transition"
                  >
                    <FileDown size={16} />
                    {generatingPdf ? "Generating..." : "Download Receipt"}
                  </button>
                )}

                <button
                  onClick={() => handleEditStudent(selectedStudent.student_id)}
                  className="bg-green-600 hover:bg-green-700 text-white px-4 py-1.5 rounded-md text-sm font-medium transition"
                >
                  Edit
                </button>
                <button
                  onClick={closeModal}
                  className="text-gray-600 hover:text-gray-900"
                >
                  <X size={18} />
                </button>
              </div>
            </div>

            <div className="p-6">
              {/* Personal & Course */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-6 mb-8">
                <div>
                  <h4 className="font-semibold text-gray-700 mb-3 border-b pb-2">
                    Personal Information
                  </h4>
                  <div className="space-y-2 text-sm">
                    <p>
                      <strong>Student Name:</strong>{" "}
                      {displayValue(selectedStudent.student_name)}
                    </p>
                    <p>
                      <strong>Father Name:</strong>{" "}
                      {displayValue(selectedStudent.father_name)}
                    </p>
                    <p>
                      <strong>Mother Name:</strong>{" "}
                      {displayValue(selectedStudent.mother_name)}
                    </p>
                    <p>
                      <strong>Date of Birth:</strong>{" "}
                      {selectedStudent.date_of_birth
                        ? new Date(selectedStudent.date_of_birth)
                            .toLocaleDateString("en-GB", {
                              day: "2-digit",
                              month: "short",
                              year: "numeric",
                            })
                            .replace(/ /g, "-")
                        : "N/A"}
                    </p>
                    <p>
                      <strong>Caste:</strong>{" "}
                      {displayValue(selectedStudent.caste)}
                    </p>
                    <p>
                      <strong>Religion:</strong>{" "}
                      {displayValue(selectedStudent.religion)}
                    </p>
                    <p>
                      <strong>Email:</strong>{" "}
                      {displayValue(selectedStudent.email)}
                    </p>
                    <p>
                      <strong>Aadhar Number:</strong>{" "}
                      {displayValue(selectedStudent.aadhar)}
                    </p>
                  </div>
                </div>
                <div>
                  <h4 className="font-semibold text-gray-700 mb-3 border-b pb-2">
                    Course Details
                  </h4>
                  <div className="space-y-2 text-sm">
                    <p>
                      <strong>Degree:</strong>{" "}
                      {displayValue(selectedStudent.degree)}
                    </p>
                    <p>
                      <strong>Stream:</strong>{" "}
                      {displayValue(selectedStudent.stream)}
                    </p>
                    <p>
                      <strong>Academic Year:</strong>{" "}
                      {displayValue(selectedStudent.academic_year)}
                    </p>
                  </div>
                </div>
              </div>

              {/* Contact & Address */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-6 mb-8">
                <div>
                  <h4 className="font-semibold text-gray-700 mb-3 border-b pb-2">
                    Contact Details
                  </h4>
                  <div className="space-y-2 text-sm">
                    <p>
                      <strong>Mobile Number:</strong>{" "}
                      {displayValue(selectedStudent.mobile_number)}
                    </p>
                    <p>
                      <strong>Parent Mobile Number:</strong>{" "}
                      {displayValue(selectedStudent.alternate_mobile_number)}
                    </p>
                  </div>
                </div>
                <div>
                  <h4 className="font-semibold text-gray-700 mb-3 border-b pb-2">
                    Address Details
                  </h4>
                  <div className="space-y-2 text-sm">
                    <p>
                      <strong>State:</strong>{" "}
                      {displayValue(selectedStudent.state)}
                    </p>
                    <p>
                      <strong>District:</strong>{" "}
                      {displayValue(selectedStudent.district)}
                    </p>
                    <p>
                      <strong>City:</strong>{" "}
                      {displayValue(selectedStudent.city)}
                    </p>
                    <p>
                      <strong>Village:</strong>{" "}
                      {displayValue(selectedStudent.village)}
                    </p>
                                          <p>
                      <strong>Address:</strong>{" "}
                      {displayValue(selectedStudent.address)}
                    </p>
                    <p>
                      <strong>Pincode:</strong>{" "}
                      {displayValue(selectedStudent.pincode)}
                    </p>
       
                  </div>
                </div>
              </div>

              {/* Lead By */}
              <div className="mb-8">
                <h4 className="font-semibold text-gray-700 mb-3 border-b pb-2">
                  Lead By
                </h4>
                <div className="text-sm">
                  <p>
                    <strong>Lead By:</strong>{" "}
                    <span className="inline-block px-3 py-1 text-xs font-medium rounded-full bg-blue-50 text-blue-700">
                      {displayValue(selectedStudent.lead_by)}
                    </span>
                  </p>
                </div>
              </div>

              {/* Payment Information */}
              <div className="mb-8">
                <h4 className="font-semibold text-gray-700 mb-4 border-b pb-2">
                  Payment Information
                </h4>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                  <div>
                    <p className="text-sm font-medium text-gray-600">
                      UTR Number
                    </p>
                    <p className="mt-1">
                      {displayValue(selectedStudent.utr_number)}
                    </p>
                  </div>
                  <div>
                    <p className="text-sm font-medium text-gray-600">
                      Transaction ID
                    </p>
                    <p className="mt-1">
                      {displayValue(selectedStudent.transaction_id)}
                    </p>
                  </div>
                  <div>
                    <p className="text-sm font-medium text-gray-600">Amount</p>
                    <p className="mt-1 font-semibold text-green-700">
                      ₹{displayValue(selectedStudent.amount)}
                    </p>
                  </div>
                  <div>
                    <p className="text-sm font-medium text-gray-600">
                      Payment Status
                    </p>
                    <p className="mt-1">
                      <span
                        className={`inline-block px-3 py-1 text-xs font-medium rounded-full ${selectedStudent.is_paid === "1" ? "bg-green-100 text-green-800" : "bg-red-100 text-red-800"}`}
                      >
                        {selectedStudent.is_paid === "1" ? "Paid" : "Unpaid"}
                      </span>
                    </p>
                  </div>
                  <div>
                    <p className="text-sm font-medium text-gray-600">
                      Payment Date
                    </p>
                    <p className="mt-1">
                      {selectedStudent.payment_date
                        ? new Date(selectedStudent.payment_date).toLocaleString(
                            "en-IN",
                          )
                        : "N/A"}
                    </p>
                  </div>
                  <div>
                    <p className="text-sm font-medium text-gray-600">
                      Registered On
                    </p>
                    <p className="mt-1">
                      {displayValue(selectedStudent.registered_on)}
                    </p>
                  </div>
                </div>
              </div>

              {/* ── Admission & Reporting Status ── */}
              <div className="mb-8">
                <h4 className="font-semibold text-gray-700 mb-4 border-b pb-2">
                  Admission &amp; Reporting Status
                </h4>
                <div className="flex items-center gap-8">
                  <div className="flex items-center gap-4">
                    <span className="text-sm font-medium text-gray-600">
                      Admission:
                    </span>
                    {renderAdmissionStatus(selectedStudent.admission_done)}
                  </div>
                  <div className="flex items-center gap-4">
                    <span className="text-sm font-medium text-gray-600">
                      Reporting:
                    </span>
                    {renderReportStatus(selectedStudent.is_reported)}
                  </div>
                </div>
              </div>

              {/* Payment Receipt Image */}
              <div>
                <h4 className="font-semibold text-gray-700 mb-3 border-b pb-2">
                  Payment Receipt
                </h4>
                <div className="border rounded-lg p-4 bg-gray-50">
                  {loadingReceipt ? (
                    <div className="text-center py-4">
                      <div className="inline-block animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600"></div>
                      <p className="mt-2 text-sm text-gray-600">
                        Loading receipt...
                      </p>
                    </div>
                  ) : receiptImageUrl ? (
                    <div>
                      <div className="mb-4 flex justify-center">
                        <img
                          src={receiptImageUrl}
                          alt="Payment Receipt"
                          className="max-w-full h-auto rounded-lg border border-gray-300 shadow-sm cursor-pointer"
                          style={{ maxHeight: "400px" }}
                          onError={(e) => {
                            console.error(
                              "Image failed to load:",
                              receiptImageUrl,
                            );
                            e.target.style.display = "none";
                            const parent = e.target.parentElement;
                            if (parent) {
                              const errorMsg = document.createElement("p");
                              errorMsg.className = "text-red-500 text-center";
                              errorMsg.innerText =
                                "Failed to load receipt image";
                              parent.appendChild(errorMsg);
                            }
                            setReceiptImageUrl(null);
                          }}
                          onClick={() => handleViewReceipt()}
                        />
                      </div>
                      <div className="flex gap-4 justify-center">
                        <button
                          onClick={handleViewReceipt}
                          className="flex items-center gap-1 px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded-md text-sm font-medium transition"
                        >
                          <Eye size={16} /> View Full Size
                        </button>
                        <button
                          onClick={handleDownloadReceipt}
                          className="flex items-center gap-1 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-md text-sm font-medium transition"
                        >
                          <Download size={16} /> Download Receipt
                        </button>
                      </div>
                    </div>
                  ) : (
                    <div className="text-center py-8">
                      <p className="text-gray-500">
                        No payment receipt available for this student
                      </p>
                      {selectedStudent.is_paid !== "1" && (
                        <button
                          onClick={() => handlePayNow(selectedStudent)}
                          className="mt-3 px-4 py-2 bg-yellow-600 hover:bg-yellow-700 text-white rounded-md text-sm font-medium transition"
                        >
                          Pay Now to Generate Receipt
                        </button>
                      )}
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ── Application Form Modal ── */}
      {showAppFormModal && appFormStudent && (
        <div className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-2xl max-w-4xl w-full max-h-[92vh] overflow-y-auto">
            <div className="no-print sticky top-0 bg-white border-b px-6 py-4 flex justify-between items-center z-10">
              <h3 className="text-lg font-bold text-gray-800">
                Application Form - {displayValue(appFormStudent.student_name)}
              </h3>
              <div className="flex items-center gap-3">
                <button
                  onClick={handleDownloadApplicationFormPDF}
                  disabled={generatingAppFormPdf}
                  className="flex items-center gap-1.5 bg-purple-600 hover:bg-purple-700 disabled:bg-purple-400 text-white px-4 py-1.5 rounded-md text-sm font-medium transition"
                >
                  <FileDown size={16} />
                  {generatingAppFormPdf ? "Generating..." : "Download PDF"}
                </button>
                <button
                  onClick={handlePrintApplicationForm}
                  className="flex items-center gap-1.5 bg-blue-600 hover:bg-blue-700 text-white px-4 py-1.5 rounded-md text-sm font-medium transition"
                >
                  <Printer size={16} /> Print
                </button>
                <button
                  onClick={closeAppFormModal}
                  className="text-gray-600 hover:text-gray-900"
                >
                  <X size={18} />
                </button>
              </div>
            </div>

            <div
              id="application-form-print"
              ref={appFormRef}
              className="p-6 bg-white"
            >
              <div
                className="flex items-center justify-between border-b pb-3 mb-4"
                style={{ borderColor: "#e5e7eb" }}
              >
                <div className="flex-shrink-0">
                  <img
                    src={ghrLogo}
                    alt="University Logo"
                    className="h-14 w-auto"
                  />
                </div>

                <div className="text-right flex-1 ml-4">
                  <p
                    className="font-bold text-base"
                    style={{ color: "#3b1a6b" }}
                  >
                    GH Raisoni University, Saikheda Application Form
                  </p>
                  <p
                    className="text-xs font-medium"
                    style={{ color: "#f5d87a" }}
                  >
                    Admission 2026–27
                  </p>
                </div>
              </div>

              <p className="text-xs text-gray-600 mb-4">
                This document contains the complete application details
                submitted by{" "}
                <strong style={{ color: "#3b1a6b" }}>
                  {displayValue(appFormStudent.student_name)}
                </strong>{" "}
                at{" "}
                <strong style={{ color: "#3b1a6b" }}>
                  GH Raisoni University, Saikheda
                </strong>
                .
              </p>

              <div className="mb-4">
                <div
                  className="app-form-header text-white text-sm font-semibold px-4 py-2 rounded-t-md"
                  style={{ background: "#3b1a6b" }}
                >
                  COURSE APPLYING FOR
                </div>
                <div
                  className="border border-t-0 rounded-b-md px-4 py-3 text-sm"
                  style={{ borderColor: "#3b1a6b" }}
                >
                  UG &gt; School of Engineering &amp; Technology &gt; B.Tech. in{" "}
                  {displayValue(appFormStudent.stream)}
                </div>
              </div>

              <div className="mb-4">
                <div
                  className="app-form-subheader text-white text-sm font-semibold px-4 py-2 rounded-t-md"
                  style={{ background: "#4e2280" }}
                >
                  PERSONAL DETAILS
                </div>
                <div
                  className="border border-t-0 rounded-b-md px-4 py-4"
                  style={{ borderColor: "#4e2280" }}
                >
                  <div className="grid grid-cols-2 gap-x-8 gap-y-2 text-sm">
                    <p>
                      <strong>Full Name:</strong>{" "}
                      {displayValue(appFormStudent.student_name)}
                    </p>
                    <p>
                      <strong>Mobile Number:</strong>{" "}
                      {displayValue(appFormStudent.mobile_number)}
                    </p>
                    <p>
                      <strong>Father's Name:</strong>{" "}
                      {displayValue(appFormStudent.father_name)}
                    </p>
                    <p>
                      <strong>Mother's Name:</strong>{" "}
                      {displayValue(appFormStudent.mother_name)}
                    </p>
                    <p>
                      <strong>Date of Birth:</strong>{" "}
                      {formatDMY(appFormStudent.date_of_birth)}
                    </p>
                    <p>
                      <strong>Caste:</strong>{" "}
                      {displayValue(appFormStudent.caste)}
                    </p>
                    <p>
                      <strong>Religion:</strong>{" "}
                      {displayValue(appFormStudent.religion)}
                    </p>
                    <p>
                      <strong>Nationality:</strong>{" "}
                      {displayValue(appFormStudent.nationality)}
                    </p>
                    <p>
                      <strong>Email ID:</strong>{" "}
                      {displayValue(appFormStudent.email)}
                    </p>
                    <p>
                      <strong>Parents Mobile Number:</strong>{" "}
                      {displayValue(appFormStudent.alternate_mobile_number)}
                    </p>
                    <p className="col-span-2">
                      <strong>UIDAI Number / Aadhaar No:</strong>{" "}
                      {displayValue(appFormStudent.aadhar)}
                    </p>
                  </div>
                </div>
              </div>

              <div className="mb-4">
                <div
                  className="app-form-subheader text-white text-sm font-semibold px-4 py-2 rounded-t-md"
                  style={{ background: "#4e2280" }}
                >
                  ADDRESS DETAILS
                </div>
                <div
                  className="border border-t-0 rounded-b-md px-4 py-4"
                  style={{ borderColor: "#4e2280" }}
                >
                  <div className="grid grid-cols-2 gap-x-8 gap-y-2 text-sm">
                    <p className="col-span-2">
                      <strong>Address:</strong>{" "}
                      {displayValue(appFormStudent.address_line1)}
                    </p>
                    <p>
                      <strong>City:</strong> {displayValue(appFormStudent.city)}
                    </p>
                    <p>
                      <strong>District:</strong>{" "}
                      {displayValue(appFormStudent.district)}
                    </p>
                    <p>
                      <strong>State:</strong>{" "}
                      {displayValue(appFormStudent.state)}
                    </p>
                    <p>
                      <strong>Country:</strong>{" "}
                      {displayValue(appFormStudent.country)}
                    </p>
                    <p>
                      <strong>PIN Code:</strong>{" "}
                      {displayValue(appFormStudent.pincode)}
                    </p>
                  </div>
                </div>
              </div>

              <div className="mb-4">
                <div
                  className="app-form-subheader text-white text-sm font-semibold px-4 py-2 rounded-t-md"
                  style={{ background: "#4e2280" }}
                >
                  ADMISSION STATUS
                </div>
                <div
                  className="border border-t-0 rounded-b-md px-4 py-3 text-sm"
                  style={{ borderColor: "#4e2280" }}
                >
                  <div className="flex items-center gap-6">
                    <div className="flex items-center gap-2">
                      <span className="font-medium">Admission:</span>
                      {renderAdmissionStatus(appFormStudent.admission_done)}
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="font-medium">Reporting:</span>
                      {renderReportStatus(appFormStudent.is_reported)}
                    </div>
                  </div>
                </div>
              </div>

              <div className="mb-4">
                <div
                  className="app-form-subheader text-white text-sm font-semibold px-4 py-2 rounded-t-md"
                  style={{ background: "#4e2280" }}
                >
                  DECLARATION
                </div>
                <div
                  className="border border-t-0 rounded-b-md px-4 py-3 text-sm"
                  style={{ borderColor: "#4e2280" }}
                >
                  <p className="text-gray-700">
                    I hereby declare that the information furnished by me in the
                    admission application submitted to GH Raisoni University,
                    Saikheda is true and correct to the best of my knowledge and
                    belief. I understand that any false or misleading
                    information may result in the cancellation of my admission.
                  </p>
                </div>
              </div>

              <div className="mb-6">
                <div
                  className="app-form-header text-white text-sm font-semibold px-4 py-2 rounded-t-md"
                  style={{ background: "#3b1a6b" }}
                >
                  PAYMENT DETAILS
                </div>
                <div
                  className="border border-t-0 rounded-b-md px-4 py-4"
                  style={{ borderColor: "#3b1a6b" }}
                >
                  <div className="grid grid-cols-2 gap-x-8 gap-y-2 text-sm">
                    <p>
                      <strong>Payment Status:</strong>{" "}
                      <span
                        className={`inline-block px-2 py-0.5 text-xs font-medium rounded-full ${appFormStudent.is_paid === "1" ? "bg-green-100 text-green-800" : "bg-red-100 text-red-800"}`}
                      >
                        {appFormStudent.is_paid === "1" ? "Success" : "Pending"}
                      </span>
                    </p>
                    <p>
                      <strong>Total Amount:</strong> ₹
                      {displayValue(appFormStudent.amount)}
                    </p>
                    <p>
                      <strong>Payment Method:</strong> web
                    </p>
                    <p>
                      <strong>Transaction ID:</strong>{" "}
                      {displayValue(appFormStudent.transaction_id)}
                    </p>
                    <p>
                      <strong>UTR Number:</strong>{" "}
                      {displayValue(appFormStudent.utr_number)}
                    </p>
                    <p>
                      <strong>Payment Date:</strong>{" "}
                      {appFormStudent.payment_date
                        ? new Date(
                            appFormStudent.payment_date,
                          ).toLocaleDateString("en-GB", {
                            day: "2-digit",
                            month: "long",
                            year: "numeric",
                          })
                        : "N/A"}
                    </p>
                  </div>
                </div>
              </div>

              <div
                className="flex justify-between items-end border-t pt-4 mt-6 text-sm"
                style={{ borderColor: "#e5e7eb" }}
              >
                <div>
                  <p className="text-gray-700">Applicant:</p>
                  <p className="font-semibold" style={{ color: "#3b1a6b" }}>
                    {displayValue(appFormStudent.student_name)}
                  </p>
                </div>
                <div className="text-right">
                  <p className="text-gray-700">Date:</p>
                  <p className="font-semibold" style={{ color: "#3b1a6b" }}>
                    {new Date().toLocaleDateString("en-GB", {
                      day: "2-digit",
                      month: "long",
                      year: "numeric",
                    })}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default StudentReport;